/* useBodyScrollLock.js — iter-171 operational polish.
 *
 * Robust, ref-counted body scroll lock for any modal / sheet / drawer
 * that mounts on top of the workspace. Without this, iOS Safari lets
 * the page underneath scroll while a modal is open (causing accidental
 * background interactions and a "the modal isn't really blocking"
 * feeling). With it, the page stays still until the modal dismisses.
 *
 * Why a hook (not a global CSS rule):
 *   - Nested / sequential modals must not race each other. The first
 *     modal locks the body; the second one increments the lock count;
 *     unmounting the second decrements but the body stays locked
 *     until the first also unmounts. Standard ref-counted semantics.
 *   - We preserve the original overflow value so we restore it
 *     exactly — never blow away a custom value set elsewhere.
 *   - Records scroll position before locking + restores it after.
 *     iOS Safari otherwise jumps to the top of the page when the
 *     body becomes scroll-locked.
 *
 * Usage:
 *
 *     export function MyDialog() {
 *       useBodyScrollLock(true);   // active while mounted
 *       return <div role="dialog" ... />;
 *     }
 *
 *     export function MaybeOpenDialog({ open }) {
 *       useBodyScrollLock(open);   // toggles as the prop flips
 *       return open ? <div ... /> : null;
 *     }
 */
import { useEffect } from "react";

let _lockCount = 0;
let _prevBodyOverflow = "";
let _prevBodyPosition = "";
let _prevBodyTop      = "";
let _prevBodyWidth    = "";
let _scrollY = 0;

const _applyLock = () => {
  if (typeof document === "undefined") return;
  if (_lockCount === 0) {
    // First lock — capture current state.
    _scrollY          = window.scrollY || window.pageYOffset || 0;
    _prevBodyOverflow = document.body.style.overflow;
    _prevBodyPosition = document.body.style.position;
    _prevBodyTop      = document.body.style.top;
    _prevBodyWidth    = document.body.style.width;
    // The classic "fixed-position body" trick prevents iOS Safari
    // from rubber-banding behind the modal. Pair with `top: -scrollY`
    // so the visible viewport doesn't jump.
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top      = `-${_scrollY}px`;
    document.body.style.width    = "100%";
  }
  _lockCount += 1;
};

const _releaseLock = () => {
  if (typeof document === "undefined") return;
  if (_lockCount === 0) return;
  _lockCount -= 1;
  if (_lockCount === 0) {
    // Last unlock — restore exactly what was there.
    document.body.style.overflow = _prevBodyOverflow;
    document.body.style.position = _prevBodyPosition;
    document.body.style.top      = _prevBodyTop;
    document.body.style.width    = _prevBodyWidth;
    // Restore scroll position. The `auto` behaviour matters — iOS
    // jumps if we use 'smooth' here.
    window.scrollTo({ top: _scrollY, left: 0, behavior: "auto" });
  }
};

/** Toggle body scroll lock for the lifetime of the calling component
 *  (or while `active` is true). Safe to call multiple times across
 *  nested modals — uses internal ref counting. */
export function useBodyScrollLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    _applyLock();
    return () => _releaseLock();
  }, [active]);
}

export default useBodyScrollLock;
